import { z } from 'zod';
import { PROGRAM_SEED } from '../programs/seed-data';

// Technique catalogue entry schema — the ADR-109 field set as code, following
// the program_definitions narrow-waist precedent (packages/shared/src/programs):
// strict everywhere (unknown keys reject, never silently strip), single-source
// const arrays for the governed enums, entries validated by the co-located test
// suite rather than a runtime throw.
//
// Every field is prompt-strength: whatever an entry carries flows into the
// practice payload the voice agent reads, so member-facing utterances are
// confined to `firstMove` (the one signed verbatim field) and the two
// machine-readable exception forms below. The co-located scanner enforces the
// authoring rules the schema cannot express structurally.

// ─── governed enums (single source of truth, ADR-056 Rule 8 style) ────────────
export const TECHNIQUE_APPLIES_TO = ['base', 'journey'] as const;
export const techniqueAppliesToSchema = z.enum(TECHNIQUE_APPLIES_TO);
export type TechniqueAppliesTo = z.infer<typeof techniqueAppliesToSchema>;

// Stable snake_case identifier shape — shared by the entry `key` and every
// field that references entries by key (on_record evidence).
export const TECHNIQUE_KEY_PATTERN = /^[a-z][a-z0-9]*(_[a-z0-9]+)*$/;

export const REQUIRES_FIRST_KIND = ['on_record', 'conversation'] as const;
export const requiresFirstKindSchema = z.enum(REQUIRES_FIRST_KIND);
export type RequiresFirstKind = z.infer<typeof requiresFirstKindSchema>;

// ─── program-key vocabulary (member-axis allowlist) ───────────────────────────
// `programs` values must be canonical program_definitions keys — validated
// against the seeded catalog vocabulary so an entry cannot allowlist a program
// that does not exist. Open set at the type level (the catalog grows as data),
// closed at parse time against the seed.
const PROGRAM_KEY_VOCABULARY: ReadonlySet<string> = new Set(PROGRAM_SEED.map((row) => row.key));
export const techniqueProgramKeySchema = z
  .string()
  .refine((key) => PROGRAM_KEY_VOCABULARY.has(key), {
    message: 'programs entries must be canonical program_definitions keys',
  });

// ─── machine-readable utterance-exception forms (ADR-109 Rule 8) ──────────────
// (a) A line the MEMBER speaks or rehearses (never Sona) is carried as a typed
// span inside `method`, distinguishable from bare prose beats — so the scanner
// needs zero prose heuristics to exempt it.
export const memberVoicedSpanSchema = z.object({ memberVoiced: z.string().min(1) }).strict();
export type MemberVoicedSpan = z.infer<typeof memberVoicedSpanSchema>;

// (b) A prohibition that names the banned phrase carries the phrase in its own
// sub-field — naming a banned phrase is a prohibition, not an utterance.
export const prohibitedPhraseConstraintSchema = z
  .object({ constraint: z.string().min(1), phrase: z.string().min(1) })
  .strict();
export type ProhibitedPhraseConstraint = z.infer<typeof prohibitedPhraseConstraintSchema>;

// ─── requires_first (layered gate) ────────────────────────────────────────────
// `on_record` clauses are gate-checked in code, fail-closed, against structured
// sources; `conversation` clauses are judgement rules handed to the selection
// call, verified against the intent/anchor rather than trusted from them.
//
// ADR-109 Rule 4: a clause names EVIDENCE, never provenance — entry names
// appear in the prose only parenthetically, if at all. The gate therefore
// verifies an `on_record` clause STRUCTURALLY, never by parsing the prose:
// the clause carries a machine-readable `evidence` spec naming exactly what
// must be on record, and `clause` stays the authored clinical prose. Today
// the builder's own build log is the only structured source
// (`source: 'prior_build'`); new sources widen the literal when their tables
// exist — never fabricated ahead of the data.
export const onRecordEvidenceSchema = z
  .object({
    source: z.literal('prior_build'),
    // Catalogue entry keys that must each be on record (a prior `built`
    // decision for the member) before the clause verifies.
    techniqueKeys: z
      .array(
        z
          .string()
          .regex(TECHNIQUE_KEY_PATTERN, 'evidence techniqueKeys must be snake_case entry keys'),
      )
      .nonempty(),
  })
  .strict();
export type OnRecordEvidence = z.infer<typeof onRecordEvidenceSchema>;

export const requiresFirstClauseSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('conversation'), clause: z.string().min(1) }).strict(),
  z
    .object({
      kind: z.literal('on_record'),
      clause: z.string().min(1),
      evidence: onRecordEvidenceSchema,
    })
    .strict(),
]);
export type RequiresFirstClause = z.infer<typeof requiresFirstClauseSchema>;

// ─── signature (per version; null = unsigned draft) ───────────────────────────
export const techniqueSignatureSchema = z
  .object({ signedBy: z.string().min(1), signedAt: z.string().min(1) })
  .strict();
export type TechniqueSignature = z.infer<typeof techniqueSignatureSchema>;

// ─── the entry ────────────────────────────────────────────────────────────────
export const techniqueEntrySchema = z
  .object({
    // Stable snake_case identifier — decision records reference it as a string
    // (no FK), so it never changes across versions.
    key: z.string().regex(TECHNIQUE_KEY_PATTERN, 'key must be snake_case'),
    // Clinician signature is per version: any governed-field change bumps this
    // and re-obtains the signature.
    version: z.string().min(1),
    name: z.string().min(1),
    // Open string — the pillar taxonomy is deliberately undecided.
    pillar: z.string().min(1),
    // What the technique is FOR — the need it serves, angle prose only.
    serves: z.string().min(1),
    // How the technique moves — prose beats as bare strings; member-voiced
    // rehearsal lines only via the typed span (exception class a).
    method: z.array(z.union([z.string().min(1), memberVoicedSpanSchema])).nonempty(),
    // Hard prohibitions — bare-string constraints; a prohibition naming a
    // banned phrase uses the typed form (exception class b).
    never: z.array(z.union([z.string().min(1), prohibitedPhraseConstraintSchema])),
    requiresFirst: z.array(requiresFirstClauseSchema),
    // Completion markers. Member-side listen-for markers may be quoted here —
    // done_when is deliberately NOT scanned for utterances. Required non-empty
    // for signing (superRefine below), may be empty only on unsigned drafts.
    doneWhen: z.string(),
    // The ONLY verbatim Sona-facing text an entry carries — signed verbatim by
    // design; null when the technique opens compositionally.
    firstMove: z.string().min(1).nullable(),
    exemplars: z.array(z.string()),
    // Caller axis: which pathway builders may select this entry.
    appliesTo: z.array(techniqueAppliesToSchema).nonempty(),
    // Member axis: program allowlist feeding the eligibility gate; null = all
    // programs. Deliberately separate from appliesTo — never overloaded.
    programs: z.array(techniqueProgramKeySchema).nonempty().nullable(),
    signature: techniqueSignatureSchema.nullable(),
    // Provenance: the shipped prompt file(s) the entry translates, or the
    // evidence base it originates from.
    source: z.string().min(1),
  })
  .strict()
  .superRefine((entry, ctx) => {
    if (entry.signature !== null && entry.doneWhen.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['signature'],
        message: 'a signed entry requires a non-empty doneWhen (done_when is required for signing)',
      });
    }
  });
export type TechniqueEntry = z.infer<typeof techniqueEntrySchema>;
